import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { EmailField, PhoneField, TextField } from '../../../ui/fields';
import { Field, FormModal, FormSection, V } from '../../../ui/forms';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Empty,
  Input,
  Popconfirm,
  Select,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import type { CreateStaffRequest, StaffRole, StaffRow } from '@velora/contracts';
import { productApi } from '../../../api';

/**
 * Admin surface — Screen 19. Staff directory.
 * Real staff data from Setup step 3's table. Teaching-load, leave-balance, and
 * biometric-attendance columns render "—" until the HR module ships (Admin #41).
 */
const ROLE_LABELS: Record<StaffRole, string> = {
  principal: 'Principal',
  vice_principal: 'Vice principal',
  coordinator: 'Coordinator',
  teacher: 'Teacher',
  clerk: 'Clerk',
  support: 'Support',
};

export function StaffListPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [role, setRole] = useState<StaffRole | 'all'>('all');
  const [addOpen, setAddOpen] = useState(false);

  const list = useQuery({
    queryKey: ['staff', institution],
    queryFn: () => productApi.listStaff(institution),
    enabled: !!institution,
  });

  const del = useMutation({
    mutationFn: (id: string) => productApi.deleteStaff(institution, id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['staff', institution] }); void message.success('Marked inactive'); },
  });

  const rows = (list.data?.rows ?? []).filter((r) => {
    if (role !== 'all' && r.role !== role) return false;
    if (q) {
      const like = q.toLowerCase();
      if (!r.name.toLowerCase().includes(like) && !r.phone.includes(like) && !(r.email ?? '').toLowerCase().includes(like)) {
        return false;
      }
    }
    return true;
  });

  const total = list.data?.total ?? 0;
  const active = list.data?.rows.filter((r) => r.status === 'active').length ?? 0;
  const teachers = list.data?.rows.filter((r) => r.role === 'teacher' && r.status === 'active').length ?? 0;
  const admin = list.data?.rows.filter((r) => ['principal', 'vice_principal', 'coordinator', 'clerk'].includes(r.role) && r.status === 'active').length ?? 0;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Staff</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            {list.data
              ? `${total} on file · ${active} active · load, leave, attendance columns arrive with the HR module`
              : 'Loading roster…'}
          </Typography.Text>
        </div>
        <Space size={8}>
          <Button disabled title="Excel import ships with the HR module">Import from Excel</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
            Add staff
          </Button>
        </Space>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small"><Statistic title="On file" value={total} /></Card>
        <Card size="small"><Statistic title="Active" value={active} /></Card>
        <Card size="small"><Statistic title="Teachers" value={teachers} /></Card>
        <Card size="small"><Statistic title="Admin roles" value={admin} /></Card>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Input
          size="small"
          placeholder="Name, phone, email"
          prefix={<SearchOutlined style={{ color: '#8a94a6' }} />}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          allowClear
          style={{ maxWidth: 280 }}
        />
        <Select
          size="small"
          value={role}
          onChange={(v) => setRole(v)}
          style={{ width: 160 }}
          options={[
            { value: 'all', label: 'Role: all' },
            ...(Object.keys(ROLE_LABELS) as StaffRole[]).map((r) => ({ value: r, label: ROLE_LABELS[r] })),
          ]}
        />
      </div>

      <Card size="small" title={`${rows.length} shown`}>
        {list.isLoading && <Skeleton active paragraph={{ rows: 6 }} />}
        {list.isError && <Alert type="error" showIcon message="Could not load staff" description={String(list.error)} />}
        {list.data && rows.length === 0 && (
          <Empty
            style={{ padding: '20px 0' }}
            description={
              <span>
                {total === 0 ? (
                  <>No staff yet. <a onClick={() => setAddOpen(true)}>Add the first one</a>.</>
                ) : (
                  <>Nothing matches the filter.</>
                )}
              </span>
            }
          />
        )}
        {list.data && rows.length > 0 && (
          <Table<StaffRow>
            size="small"
            dataSource={rows}
            rowKey="id"
            pagination={false}
            onRow={(record) => ({
              onClick: (e) => {
                // don't hijack the delete Popconfirm click
                if ((e.target as HTMLElement).closest('.ant-btn')) return;
                navigate(`/i/${institution}/admin/staff/${record.id}`);
              },
              style: { cursor: 'pointer' },
            })}
            columns={[
              {
                key: 'photo',
                width: 40,
                render: (_, r) => (
                  <Avatar size={28} style={{ background: `hsl(${(r.name.length * 43) % 360}, 40%, 60%)`, fontSize: 11 }}>
                    {r.name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
                  </Avatar>
                ),
              },
              { title: 'Name', dataIndex: 'name', key: 'name', render: (v: string) => <span style={{ fontWeight: 500, color: '#14213d' }}>{v}</span> },
              { title: 'Role', dataIndex: 'role', key: 'role', render: (v: StaffRole) => <Tag>{ROLE_LABELS[v]}</Tag> },
              { title: 'Phone', dataIndex: 'phone', key: 'phone', width: 140, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Email', dataIndex: 'email', key: 'email', render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text> },
              { title: 'Load / wk', key: 'load', width: 90, render: () => <Typography.Text type="secondary" style={{ fontSize: 12 }}>—</Typography.Text> },
              { title: 'Leave', key: 'leave', width: 80, render: () => <Typography.Text type="secondary" style={{ fontSize: 12 }}>—</Typography.Text> },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 100,
                render: (v: string) => <Tag color={v === 'active' ? 'success' : 'default'}>{v}</Tag>,
              },
              {
                key: 'action',
                width: 40,
                align: 'right',
                render: (_, r) =>
                  r.status === 'active' ? (
                    <Popconfirm
                      title="Mark inactive?"
                      description="D-06: we deactivate, never delete."
                      onConfirm={() => del.mutate(r.id)}
                      okText="Mark inactive"
                      okButtonProps={{ danger: true }}
                    >
                      <Button size="small" type="text" icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                  ) : null,
              },
            ]}
          />
        )}
      </Card>

      <AddStaffModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        institution={institution}
        onCreated={() => {
          void qc.invalidateQueries({ queryKey: ['staff', institution] });
          setAddOpen(false);
          void message.success('Staff added');
        }}
      />
    </Space>
  );
}

type StaffFormValues = { name: string; email?: string; phone: string; role: StaffRole };

function AddStaffModal({
  open, onClose, institution, onCreated,
}: { open: boolean; onClose: () => void; institution: string; onCreated: () => void }) {
  return (
    <FormModal<StaffFormValues>
      open={open} onClose={onClose}
      title="Add staff" submitText="Add"
      initial={{ role: 'teacher' }}
      mutation={(v) => productApi.createStaff(institution, {
        name: v.name, email: v.email || undefined, phone: v.phone, role: v.role,
      } satisfies CreateStaffRequest)}
      onSuccess={onCreated}
    >
      <Field name="name" label="Full name" required>
        <TextField placeholder="A. Sharma" autoFocus />
      </Field>
      <FormSection cols={2}>
        <Field name="phone" label="Phone" required rules={[V.phone()]}>
          <PhoneField />
        </Field>
        <Field name="email" label="Email" optional rules={[V.email()]}>
          <EmailField placeholder="teacher@school.in" />
        </Field>
      </FormSection>
      <Field name="role" label="Role" required>
        <Select
          options={(Object.keys(ROLE_LABELS) as StaffRole[]).map((r) => ({ value: r, label: ROLE_LABELS[r] }))}
        />
      </Field>
    </FormModal>
  );
}
